import React from "react";

import { faCalendarPlus, faHistory, faThumbsUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import zoomSdk from "@zoom/appssdk";
import cookie from "js-cookie";

import { ReactComponent as FellowWordmark } from "~/legacy-assets/images/logos/fellow-wordmark-black-solid.svg";
import { configureZoom, zoomOpenUrl } from "~/lib/zoom";

import { AuthBase } from "../AuthBase";
import { Button } from "../components";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";

import styles from "../css/Auth.module.css";

const ZOOM_STATE_STORE_KEY = "fellow_zoom_auth_nonce"; // Also defined in python

cookie.defaults.secure = true;

function generateCodeChallenge() {
	const array = new Uint32Array(120);
	crypto.getRandomValues(array);
	return Array.from(array, s => {
		return s.toString(16).substring(0, 1);
	}).join("");
}
interface ZoomRootState {
	connectionData: string | undefined;
	isCalendarConnected: boolean | undefined;
	supportsInClientAuth: boolean | undefined;
	codeChallenge: string;
	authNonce: string;
	errorMessage: string | undefined;
}

export default class ZoomRoot extends AuthBase<AuthProps, ZoomRootState> {
	state: ZoomRootState = {
		connectionData: undefined,
		isCalendarConnected: undefined,
		supportsInClientAuth: undefined,
		codeChallenge: generateCodeChallenge(),
		authNonce: generateCodeChallenge(),
		errorMessage: undefined,
	};

	componentDidMount() {
		super.componentDidMount();

		this.zoomInit();
	}

	getPaneComponent() {
		return WhitelabelPane;
	}

	openBrowserForAuth = () => {
		if (!this.state.supportsInClientAuth) {
			zoomOpenUrl(`https://${window.location.host}/zoom/oauth/`);
		} else if (this.state.connectionData !== undefined) {
			zoomOpenUrl(
				`https://${window.location.host}/zoom/oauth/in-client-connect/?connection_data=${this.state.connectionData}`,
			);
		}
		return;
	};

	async zoomInit() {
		await configureZoom();

		zoomSdk.getSupportedJsApis().then(resp => {
			// Will be false between the time we push to prod and the time the app is approved.
			// Don't want to use methods we don't have access to, or set cookies that will break the flow.
			const supportsInClientAuth = resp.supportedApis.includes("authorize");
			this.setState({ supportsInClientAuth });
			if (supportsInClientAuth) {
				this.initZoomInClientAuth();
			}
		});
	}

	initZoomInClientAuth = () => {
		cookie.set(ZOOM_STATE_STORE_KEY, this.state.authNonce, {
			expires: 1, // 1 day
			domain: window.ROOT_DOMAIN,
			sameSite: "none",
			path: "/",
		});

		// This just kicks off the auth flow, there's nothing to handle and it'll call
		// the onAuthorized callback if it's successful
		zoomSdk.callZoomApi("authorize", {
			codeChallenge: this.state.codeChallenge,
		});

		const outerThis = this;
		zoomSdk.addEventListener("onAuthorized", function (event) {
			outerThis.fetchZoomUserId(event.code);
		});
	};

	fetchZoomUserId(code: string) {
		fetch(`/zoom/oauth/in-client/`, {
			method: "POST",
			credentials: "include",
			body: JSON.stringify({
				code: code,
				challenge: this.state.codeChallenge,
			}),
		})
			.then(response => {
				return response.json();
			})
			.then(data => {
				this.setState({
					connectionData: data.connection_data,
					isCalendarConnected: data.is_calendar_connected,
				});
			});
	}

	renderStep() {
		let buttonText: string;
		if (this.state.supportsInClientAuth === false || this.state.isCalendarConnected === true) {
			buttonText = "Log in";
		} else if (this.state.isCalendarConnected == false) {
			buttonText = "Connect your calendar";
		} else {
			buttonText = "Loading...";
		}

		return (
			<div className={styles.authFlex}>
				<FellowWordmark className={styles.wordMark}></FellowWordmark>

				<h1>Start your next Zoom call off right.</h1>

				<div className={styles.sellingPoints}>
					<div className={styles.sellingPoint}>
						<div>
							<FontAwesomeIcon icon={faUsers} />
						</div>{" "}
						Take meeting notes collaboratively
					</div>
					<div className={styles.sellingPoint}>
						<div>
							<FontAwesomeIcon icon={faCalendarPlus} />
						</div>{" "}
						Instantly create calendar events for unscheduled calls
					</div>
					<div className={styles.sellingPoint}>
						<div>
							<FontAwesomeIcon icon={faHistory} />
						</div>{" "}
						Keep track of discussions over time
					</div>
					<div className={styles.sellingPoint}>
						<div>
							<FontAwesomeIcon icon={faThumbsUp} />
						</div>{" "}
						Automatically jump into the right notes for the call
					</div>
				</div>
				<Button primary onClick={this.openBrowserForAuth}>
					{buttonText}
				</Button>
			</div>
		);
	}

	renderFooter() {
		/* This space intentionally left blank */
		return <></>;
	}
}
